// Avoid re-exports in favor of direct imports or entry-point files
/* eslint-disable no-barrel-files/no-barrel-files */

/* eslint-disable no-restricted-syntax */
import type {
	CustomPerformanceEventFactoryConfig,
	InteractionPerformanceEventFactoryConfig,
	PageLoadPerformanceEventFactoryConfig,
	PageSegmentLoadPerformanceEventFactoryConfig,
} from '@atlassian/browser-metrics/factory/types';
import type { PageLoadMetric } from '@atlassian/browser-metrics/page-load-metric';
import { metrics } from '@atlassian/browser-metrics/factory';
import { browserMetrics } from '@atlassian/browser-metrics/lib';
import type { Metric } from '@atlassian/browser-metrics/metric';

import { namedRoutes } from '@confluence/named-routes/entry-points/named-routes.ts';

import { createPageSegmentLoadMetric as createGatedPageSegmentLoadMetric } from './createGatedPageSegmentLoadMetric.ts';

export const BM_CONFIGURED_ROUTES: Set<string> = new Set([
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.CREATE_BLOG.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.CREATE_BLOG_ACTION_LEGACY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.CREATE_PAGE.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.CREATE_PAGE_ACTION_LEGACY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.EDIT_BLOG.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.EDIT_BLOG_V2.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.EDIT_PAGE.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.EDIT_PAGE_V2.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.EDIT_PAGE_EMBED.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.VIEW_PAGE.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.VIEW_BLOG.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.VIEW_BLOG_DATE_LEGACY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.SPACE_INDEX.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.SPACE_OVERVIEW.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.TEMPLATE_GALLERY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.CONTENT_HISTORY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.ADVANCED_SEARCH.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.HOME.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.SPACE_DIRECTORY.name,
	// oxlint-disable-next-line eslint-core/no-restricted-syntax
	namedRoutes.SPACE_PAGES.name,
]);

export const isBMPageLoad = (match: { name: string } | null | undefined): boolean => {
	if (!match) {
		return false;
	}
	return BM_CONFIGURED_ROUTES.has(match.name);
};

export const hasBMPageLoadStarted = (): boolean => {
	const currentMetric = browserMetrics.getPageLoadMetric();
	return currentMetric.getData().start !== null;
};

export { getWaterfallTimings } from './getWaterfallTimings.ts';

const pageLoad: (config: PageLoadPerformanceEventFactoryConfig) => PageLoadMetric =
	metrics.pageLoad;
const custom: (config: CustomPerformanceEventFactoryConfig) => Metric = metrics.custom;
const interaction: (config: InteractionPerformanceEventFactoryConfig) => Metric =
	metrics.interaction;
const concurrent: {
	pageSegmentLoad: (
		config: PageSegmentLoadPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
	interaction: (
		config: InteractionPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
	custom: (
		config: CustomPerformanceEventFactoryConfig,
	) => (concurrentId: string | number) => Metric;
} = metrics.concurrent;
const concurrentCustom: (
	config: CustomPerformanceEventFactoryConfig,
) => (concurrentId: string | number) => Metric = concurrent.custom;

export { pageLoad as createPageLoadMetric };
export { interaction as createInteractionMetric };

/**
 * Public page-segment-load factory. Backed by the scope-selecting wrapper in
 * `./createGatedPageSegmentLoadMetric.ts`. Both owner and segment in bm3MigrationConfig.ts
 * must match to select the UFO marks/data path at start.
 * Other runs keep BM3. The destination stays bound to the run through stop/cancel/marks.
 * The listed View Page segments permanently use this path. Marks do not replace native segment
 * events; full offboarding requires reporting parity with BM3 initialization disabled and parent removal.
 *
 * If you genuinely need the raw, ungated BM3 factory (e.g. parity tests), import
 * `createBM3PageSegmentLoadMetricRaw` from
 * `@confluence/browser-metrics/entry-points/createBM3PageSegmentLoadMetricRaw.ts`.
 *
 * See `next/packages/browser-metrics/AGENTS.md` → Phase 3 for context.
 */
export { createGatedPageSegmentLoadMetric as createPageSegmentLoadMetric };

export { custom as createCustomMetric };
export { concurrentCustom as createConcurrentCustomMetric };
